import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import parse from 'html-react-parser';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useReducer, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import Grid from '@material-ui/core/Grid';

import { BasicDrawer, Secure2uDrawer, TacDrawer } from 'components/common/drawers';
import { BasicButton } from 'components/common/buttons';
import { FormModal, PromptModal } from 'components/common/modals';
import { DynoConfirmationCards, DynoFavouriteResultDrawer } from 'components/dyno-template';

import {
  deleteProceedHandler,
  deleteTacSubmitHandler,
  favouriteRequestTacHandler,
  editFavouriteResultHandler,
  favouriteSubmitConfirmHandler,
  editFavouriteRequestTacHandler,
  favouriteBeneficiaryDetailsHandler,
} from 'utils/favourite/transfer/handler';
import { get } from 'utils/lodash';
import { formatErrorMessage } from 'utils/formatter';
import { getFavouriteAccounts } from 'middleware/actions/favourite/transfer';
import { getSenderAccount, putResetTransfer } from 'middleware/actions/transfer';
import { getResultDetail, getFavouriteAccountInfo } from 'utils/transfer/getter';

import { TAC_STEP_FINAL, TAC_STEP_INITIAL, TAC_STEP_RETRY } from 'settings/constants/tac';
import { getCheckUserSecure2u, getPoolingSecure2u, getPushContentSecure2u } from 'middleware/actions/sec2u';
import { REJECT_TRANSACTION_SECURE2U_RESPONSE_CODE, SUCCESS_RESPONSE_CODE } from 'settings/constants/response-codes';

const FavouriteConfirmation = ({
  userId,
  onEdit,
  onError,
  onChange,
  tacDetail,
  formDetail,
  resultDetail,
  isTacSuccess,
  isAddNewFavourite,
  isEditFavourite,
  onCancelTransfer,
  isConfirmSuccess,
  confirmationDetail,
  isFromTransferResult,
  isDeleteFavourite,
  favouriteTransferType,
  resetTransferType,
  handleTutorial,
  handleCloseCoachmark,
}) => {
  const dispatch = useDispatch();

  const { pushContentSecure2u, poolingSecure2u, checkUserSecure2u } = useSelector(
    state => ({
      pushContentSecure2u: state.Sec2uReducer.pushContentSecure2u,
      poolingSecure2u: state.Sec2uReducer.poolingSecure2u,
      checkUserSecure2u: state.Sec2uReducer.checkUserSecure2u,
    }),
    shallowEqual
  );

  const { formatMessage } = useIntl();

  const [state, setState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
    tacStep: TAC_STEP_INITIAL,
    isTacOpen: false,
    isResultOpen: false,
    timestamp: '',
    isDeleteConfirmModalOpen: false,
  });

  const { tacStep, timestamp, isTacOpen, isResultOpen, isLeavePageBlocked, isDeleteConfirmModalOpen } = state;

  const [isPolling, setIsPolling] = useState(false);
  const [openDrawerErr, setOpenDrawerErr] = useState(false);
  const [openDrawerErrCheckUser, setOpenDrawerErrCheckUser] = useState(false);
  const [backRetrySecure2u, setBackRetrySecure2u] = useState(false);
  const prevIsTacRequestSuccess = useRef(false);
  const hasDispatchedRef = useRef(false);

  const referenceNumber = get(resultDetail, 'referenceNumber', '');
  const referenceNumberDisplay = [
    {
      title: <FormattedMessage id="transaction.transfer.referenceNumber" />,
      info: referenceNumber,
    },
  ];

  const isResultTransacted = !isEmpty(resultDetail);
  const result = getResultDetail(resultDetail, favouriteTransferType);
  const receiverDetail = getFavouriteAccountInfo(favouriteTransferType, isAddNewFavourite, confirmationDetail);
  const isTransactionSuccecssful = get(resultDetail, 'isTransactionSuccessful', false);
  const beneficiaryDetails = favouriteBeneficiaryDetailsHandler(favouriteTransferType, confirmationDetail);
  const beneficiaryDetailsDisplay = [...beneficiaryDetails, ...(referenceNumber ? referenceNumberDisplay : [])];

  const { resultError, isTacLocked, isResultSuccess } = result;

  const tacBean = get(confirmationDetail, 'tacBean', {});
  const serviceName = get(confirmationDetail, 'serviceName', '');
  const tacTimestamp = get(tacDetail, 'tacSentDateTime', '');
  const { responseCode: responsePushContent } = pushContentSecure2u;

  const handleModalClose = () => setState({ ...state, isDeleteConfirmModalOpen: false });
  const handleDeleteClick = () => deleteProceedHandler(favouriteTransferType, userId, tacBean);
  const showErrorMessage = (errorCode, params) =>
    onError(parse(formatErrorMessage(formatMessage, errorCode, params)), 'error', isTacLocked);
  const retrySecure2uHandler = () => dispatch(getCheckUserSecure2u());

  const handleCloseDrawerErr = () => {
    setOpenDrawerErr(false);
    setOpenDrawerErrCheckUser(false);
    setBackRetrySecure2u(true);

    Object.keys(pushContentSecure2u).forEach(key => delete pushContentSecure2u[key]);
    Object.keys(poolingSecure2u).forEach(key => delete poolingSecure2u[key]);
    // eslint-disable-next-line no-param-reassign
    Object.keys(tacDetail).forEach(key => delete tacDetail[key]);
    Object.keys(checkUserSecure2u).forEach(key => delete checkUserSecure2u[key]);

    setState({
      tacStep: TAC_STEP_INITIAL,
      isTacOpen: true,
    });
  };

  const memoizedProps = useMemo(
    () => ({
      isTacSuccess,
      responsePushContent,
    }),
    [isTacSuccess, responsePushContent]
  );

  const handleDispatch = useCallback(() => {
    dispatch(getPushContentSecure2u({ userId, transactionType: serviceName, amount: '-' }));
  }, []);

  const handleRequestTac = () => {
    if (isDeleteFavourite) {
      return tacStep === TAC_STEP_INITIAL ? setState({ ...state, isDeleteConfirmModalOpen: true }) : handleDeleteClick();
    }

    if (isEditFavourite) {
      return editFavouriteRequestTacHandler(favouriteTransferType, { userId, tacBean });
    }

    return favouriteRequestTacHandler(favouriteTransferType, { userId, tacBean });
  };

  const handleSecure2u = () => {
    if (isDeleteFavourite) {
      return tacStep === TAC_STEP_INITIAL ? setState({ ...state, isDeleteConfirmModalOpen: true }) : handleDeleteClick();
    }

    if (isEditFavourite) {
      return editFavouriteRequestTacHandler(favouriteTransferType, { userId, tacBean });
    }

    return favouriteRequestTacHandler(favouriteTransferType, { userId, tacBean });
  };

  const handleSubmitTac = tacValue => {
    const resultPayload = { userId, formDetail, confirmationDetail, tacDetail, tacValue };

    if (isDeleteFavourite) {
      return deleteTacSubmitHandler(favouriteTransferType, resultPayload);
    }

    if (isEditFavourite) {
      return editFavouriteResultHandler(favouriteTransferType, resultPayload);
    }

    return favouriteSubmitConfirmHandler(favouriteTransferType, resultPayload);
  };

  const handleSubmitAddFavouriteAfterTransfer = () => {
    const resultPayload = { userId, formDetail, confirmationDetail: { ...confirmationDetail, isFromTransferResult: true } };
    favouriteSubmitConfirmHandler(favouriteTransferType, resultPayload);
  };

  const handleFavouriteResultDrawerClose = () => {
    setState({ isResultOpen: false });
    // isFavouriteTransactionCompleted = true: display success drawer on main page after complete transaction
    // isFromTransferResult = false: close modal without tac for add fav after transfer
    onChange({
      isFavouriteTransactionCompleted: true,
      isFromTransferResult: false,
      isDisplayTransferResult: true,
      isFavouriteTransactionSuccess: isTransactionSuccecssful,
    });
    resetTransferType();
    // clear all transfer state on redux and close result page
    dispatch(putResetTransfer());
    dispatch(getSenderAccount({ userId }));
    dispatch(getFavouriteAccounts({ userId }));
  };

  const showPopup = () => {
    if (window.innerWidth > 769) handleTutorial(false, true, false);
    if (window.innerWidth < 769) handleTutorial(false, true, false);
  };

  useEffect(() => {
    Object.keys(pushContentSecure2u).forEach(key => delete pushContentSecure2u[key]);
    Object.keys(poolingSecure2u).forEach(key => delete poolingSecure2u[key]);
    Object.keys(checkUserSecure2u).forEach(key => delete checkUserSecure2u[key]);
  }, []);

  useEffect(() => {
    if (isConfirmSuccess) {
      setState({ isTacOpen: true });
    }
  }, [isConfirmSuccess]);

  useEffect(() => {
    const shouldDispatch = isTacSuccess && !prevIsTacRequestSuccess.current && !hasDispatchedRef.current;

    if (shouldDispatch) {
      handleDispatch();
      hasDispatchedRef.current = true;
    }

    prevIsTacRequestSuccess.current = isTacSuccess;

    if (isTacSuccess && responsePushContent === SUCCESS_RESPONSE_CODE) {
      setState({
        ...state,
        isDeleteConfirmModalOpen: false,
        timestamp: tacTimestamp,
        tacStep: TAC_STEP_FINAL,
        isLeavePageBlocked: true,
        retrySecure2u: false,
      });
      hasDispatchedRef.current = false;
    }
  }, [memoizedProps, handleDispatch]);
  // useEffect(() => {
  //   // only update timestamp if TAC request is successful
  //   if (isTacSuccess) {
  //     setState({ ...state, isDeleteConfirmModalOpen: false, timestamp: tacTimestamp, tacStep: TAC_STEP_FINAL, isLeavePageBlocked: true });
  //   }
  // }, [isTacSuccess]);

  useEffect(() => {
    if (isResultSuccess) {
      setBackRetrySecure2u(false);
      setState({ isResultOpen: true, isLeavePageBlocked: false });
    }
  }, [isResultSuccess]);

  useEffect(() => {
    if (isTacLocked) {
      setState({ isLeavePageBlocked: false });
    }
  }, [isTacLocked]);

  useEffect(() => {
    if (resultError) {
      showErrorMessage(resultError);
    }
  }, [resultDetail, resultError]);

  useEffect(() => {
    if (tacStep === TAC_STEP_RETRY) handleCloseCoachmark();

    if (tacStep === TAC_STEP_FINAL) {
      setBackRetrySecure2u(false);
      setIsPolling(true);
    } else {
      // eslint-disable-next-line no-param-reassign
      Object.keys(tacDetail).forEach(key => delete tacDetail[key]);
      setIsPolling(false);
    }
  }, [tacStep]);

  useEffect(() => {
    const { securityHash } = pushContentSecure2u;
    const { responseCode } = poolingSecure2u;

    if (responseCode === SUCCESS_RESPONSE_CODE) {
      setIsPolling(false);
      Object.keys(pushContentSecure2u).forEach(key => delete pushContentSecure2u[key]);
      Object.keys(poolingSecure2u).forEach(key => delete poolingSecure2u[key]);

      handleSubmitTac('818888888');

      handleCloseCoachmark();
    }

    if (responseCode === REJECT_TRANSACTION_SECURE2U_RESPONSE_CODE) {
      handleCloseCoachmark();

      setOpenDrawerErr(true);
      setBackRetrySecure2u(true);
      setState({ isTacOpen: false });
      Object.keys(pushContentSecure2u).forEach(key => delete pushContentSecure2u[key]);
      Object.keys(poolingSecure2u).forEach(key => delete poolingSecure2u[key]);
      // eslint-disable-next-line no-param-reassign
      Object.keys(tacDetail).forEach(key => delete tacDetail[key]);
      setIsPolling(false);
    }

    let intervalId; // Declare intervalId outside the if block

    if (isPolling) {
      // Define the function to be called every 3 seconds
      const poolingDataSecure2u = () => {
        dispatch(
          getPoolingSecure2u({
            userId,
            securityHash,
          })
        );
      };

      // Set up the interval to call poolingDataSecure2u every 3 seconds
      intervalId = setInterval(poolingDataSecure2u, 3000);
    }

    // Cleanup function to clear the interval
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPolling, poolingSecure2u, dispatch, userId, pushContentSecure2u]);

  useEffect(() => {
    const { responseCode, isNormal } = checkUserSecure2u;

    if (responseCode === SUCCESS_RESPONSE_CODE && isNormal) {
      handleSecure2u();
      setIsPolling(false);
      Object.keys(checkUserSecure2u).forEach(key => delete checkUserSecure2u[key]);
    }

    if (responseCode === SUCCESS_RESPONSE_CODE && !isNormal) setState({ isTacOpen: false });
    if (!isEmpty(checkUserSecure2u) && responseCode !== SUCCESS_RESPONSE_CODE) setOpenDrawerErrCheckUser(true);
  }, [checkUserSecure2u]);

  return (
    <>
      <DynoConfirmationCards
        result={result}
        onEdit={onEdit}
        tacStep={tacStep}
        hasFavouriteButton={false}
        receiverDetail={receiverDetail}
        hasEditButton={!isDeleteFavourite}
        beneficiaryDetails={beneficiaryDetailsDisplay}
        transactionToLabel={formatMessage({ id: 'transaction.transfer.beneficiary.name' })}
        isDeleteTransaction={false}
      />
      <DynoFavouriteResultDrawer
        isOpen={isResultOpen}
        isEditFavourite={isEditFavourite}
        isDeleteFavourite={isDeleteFavourite}
        onClose={handleFavouriteResultDrawerClose}
        isTransactionSuccessful={isTransactionSuccecssful}
      />
      {/* isFromTransferResult = false: when not submit detail of `add fav after transfer` display modal */}
      {isConfirmSuccess && !isResultSuccess && !isFromTransferResult && (
        // <TacDrawer
        //   currentStep={tacStep}
        //   timestamp={timestamp}
        //   isOpen={isTacOpen}
        //   onRequestTac={handleRequestTac}
        //   onSubmitTac={handleSubmitTac}
        //   title={formatMessage({ id: 'transaction.transfer.tacTitle' })}
        //   description={parse(formatMessage({ id: 'transaction.transfer.tacDescription' }))}
        // />
        <Secure2uDrawer
          currentStep={tacStep}
          timestamp={timestamp}
          isOpen={isTacOpen}
          onRetrySecure2u={retrySecure2uHandler}
          onRequestSecure2u={handleSecure2u}
          onSubmitTac={handleSubmitTac}
          showPopup={showPopup}
          title={formatMessage({ id: 'transaction.transfer.tacTitle' })}
          onChange={setState}
          handleCloseCoachmark={handleCloseCoachmark}
          backRetrySecure2u={backRetrySecure2u}
        />
      )}
      {/* isResultTransacted = result is submitted dont show this drawer anymore */}
      {isFromTransferResult && !isResultTransacted && (
        <DynoFavouriteResultDrawer
          isOpen={isTacOpen}
          isFromTransferResult
          onClose={handleSubmitAddFavouriteAfterTransfer}
          isTransactionSuccessful={isTransactionSuccecssful}
        />
      )}
      <PromptModal
        isBlocked={isLeavePageBlocked}
        onCancelTransaction={onCancelTransfer}
        proceedLabel={formatMessage({ id: 'buttonText.ok' })}
        cancelLabel={formatMessage({ id: 'buttonText.cancel' })}
        title={formatMessage({ id: 'transaction.leaveAlert.title' })}
        content={formatMessage({ id: 'transaction.leaveAlert.description' })}
      />
      <FormModal
        fullWidth
        maxWidth="sm"
        hasCloseButton={false}
        onClose={handleModalClose}
        className="modal modal-prompt"
        isOpen={isDeleteConfirmModalOpen}
        title={formatMessage({ id: 'transaction.favourite.form.title.delete' })}
        description={formatMessage({ id: 'transaction.favourite.form.content.delete' })}
        footer={
          <Grid container justify="flex-end">
            <BasicButton className="transparent-bb db" onClick={handleModalClose} title={formatMessage({ id: 'buttonText.cancel' })} />
            <BasicButton className="db ph4 ml3 primary-btn" onClick={handleDeleteClick} title={formatMessage({ id: 'buttonText.ok' })} />
          </Grid>
        }
      />

      <BasicDrawer className="error" isOpen={openDrawerErr || openDrawerErrCheckUser} hideBackdrop={false} onClose={handleCloseDrawerErr}>
        <h1 className="f5 lh-copy tc white">
          {openDrawerErrCheckUser && <FormattedMessage id="errorMessages.ERR_SERVER_ERROR" />}
          {openDrawerErr && <FormattedMessage id="errorMessages.ERR_SECURE2U" />}
        </h1>
      </BasicDrawer>
    </>
  );
};

FavouriteConfirmation.defaultProps = {
  handleTutorial: null,
  handleCloseCoachmark: null,
};

FavouriteConfirmation.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  tacDetail: PropTypes.object.isRequired,
  formDetail: PropTypes.object.isRequired,
  isTacSuccess: PropTypes.bool.isRequired,
  resultDetail: PropTypes.object.isRequired,
  onCancelTransfer: PropTypes.func.isRequired,
  isAddNewFavourite: PropTypes.bool.isRequired,
  isEditFavourite: PropTypes.bool.isRequired,
  isConfirmSuccess: PropTypes.bool.isRequired,
  isDeleteFavourite: PropTypes.bool.isRequired,
  resetTransferType: PropTypes.func.isRequired,
  isFromTransferResult: PropTypes.bool.isRequired,
  confirmationDetail: PropTypes.object.isRequired,
  favouriteTransferType: PropTypes.string.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleTutorial: PropTypes.func,
  handleCloseCoachmark: PropTypes.func,
};

export default FavouriteConfirmation;
